class BagFeeManager {
  constructor() {
    if (window.gopuffShopify && window.gopuffShopify.bagFeeManager) {
      this._bagFeeSKU = window.gopuffShopify.bagFeeManager._bagFeeSKU
      this._bagFeeVariantId = window.gopuffShopify.bagFeeManager._bagFeeVariantId
      this._excludedLocations = window.gopuffShopify.bagFeeManager._excludedLocations || []
    }

    if (this._bagFeeVariantId) {
      this.init(this._bagFeeSKU, this._bagFeeVariantId, this._excludedLocations)
    }
  }

  init(bagFeeSKU, bagFeeVariantId, excludedLocations) {
    this.bagFeeSKU = bagFeeSKU
    this.bagFeeVariantId = bagFeeVariantId
    this.excludedLocations = (excludedLocations || []).map((id) => id.toString())

    subscribe(PUB_SUB_EVENTS.cartUpdate, this.enforceBagFee.bind(this))
    subscribe(PUB_SUB_EVENTS.bevmoLocationUpdate, this.enforceBagFee.bind(this))
    this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification')
    this.enforceBagFee()
    this.initialized = true
  }

  async enforceBagFee() {
    if (!this.initialized) {
      console.warn("BagFeeManager not initialized yet.")
      return
    }

    // find existing bag fee line in cart
    const cart = await this.getCartJSON(true)
    const bagFeeLine = this.findBagFeeLine(cart)

    if (!cart.items.length) {
      return
    }

    // if there's no items, remove bag fee line
    const hasRegularItems = cart.items.some((item) => !this.isBagFee(item))
    if (!hasRegularItems && bagFeeLine) {
      await this.removeCartLine(bagFeeLine.key)

      return
    }

    const location = window.bevmoLocation
    if (!location || !location.location_id || !location.fulfillment_type) {
      if (bagFeeLine) {
        await this.removeCartLine(bagFeeLine.key)
      }
      return
    }

    const locationId = location.location_id
    const fulfillmentType = location.fulfillment_type
    const bagFeeVariantId = this.bagFeeVariantId

    const requiresBagFee = this.shouldRequireBagFee(fulfillmentType, locationId)

    try {
      // if shipping, remove any bag fee
      if (fulfillmentType === "shipping" && bagFeeLine) {
        const updatedCart = await this.removeCartLine(bagFeeLine.key)
        publish(PUB_SUB_EVENTS.cartUpdate, { cart: updatedCart })
        return
      }

      // if missing info, don't do anything yet
      if (!locationId || !fulfillmentType) {
        return
      }

      // remove bag fee if not required
      if (bagFeeLine && !requiresBagFee) {
        const updatedCart = await this.removeCartLine(bagFeeLine.key)
        publish(PUB_SUB_EVENTS.cartUpdate, { cart: updatedCart })
        return
      }

      // add/update bag fee
      if (requiresBagFee && bagFeeVariantId) {
        if (!bagFeeLine) {
          await this.addCartLine(bagFeeVariantId, locationId)
          this.renderContents()
        } else if (bagFeeLine.variant_id.toString() !== bagFeeVariantId.toString() || bagFeeLine.quantity !== 1 || bagFeeLine.properties._location_id !== locationId) {
          await this.updateCartLine(bagFeeLine.key, bagFeeVariantId, locationId)
          this.renderContents()
        }
      }
    } catch (error) {
      console.error("Error managing bag fee:", error)
    }
  }

  async renderContents() {
    if (!this.cart) {
      return;
    }
    const body = JSON.stringify({
      sections: this.cart.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname
    });
    try {
      const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      const responseJson = await response.json();
      this.cart.renderContents(responseJson, this.cart.isOpen);
      this.cart.classList.toggle('is-empty', responseJson.item_count === 0);
    } catch (error) {
      console.error("Error updating cart:", error);
    }

  }

  shouldRequireBagFee(fulfillmentType, locationId) {
    if (fulfillmentType === "shipping") {
      return false
    }

    // can't add without a variant id
    if (!this.bagFeeVariantId || !locationId) {
      return false
    }

    return !this.excludedLocations.includes(locationId.toString())
  }

  async getCartJSON(refresh = false) {
    if (!refresh && window.cart) {
      return window.cart
    }

    try {
      const response = await fetch("/cart.json")
      const cart = await response.json()
      window.cart = cart
      return cart
    } catch (error) {
      console.error("Error fetching cart:", error)
      return window.cart || { items: [] }
    }
  }

  findBagFeeLine(cart) {
    return cart.items.find((item) => this.isBagFee(item))
  }

  isBagFee(item) {
    return item.variant_id.toString() === this.bagFeeVariantId.toString() || item.title.includes("City Required Bag Fee")
  }

  async addCartLine(variantId, locationId) {
    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: {
            _location_id: locationId,
          },
        }),
      })
      return await response.json()
    } catch (error) {
      console.error("Error adding bag fee to cart:", error)
      throw error
    }
  }

  async updateCartLine(key, variantId, locationId) {
    try {
      await this.removeCartLine(key)
      return await this.addCartLine(variantId, locationId)
    } catch (error) {
      console.error("Error updating bag fee in cart:", error)
      throw error
    }
  }

  async removeCartLine(key) {
    console.log("Removing bag fee line with key:", key)
    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: key,
          quantity: 0,
        }),
      })

      const responseJson = await response.json()
      this.renderContents()
      return responseJson
    } catch (error) {
      console.error("Error removing bag fee from cart:", error)
      throw error
    }
  }
}

const bagFeeManager = new BagFeeManager()
window.gopuffShopify.bagFeeManager = bagFeeManager
